import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Loading } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { CompanySettings } from "@/components/CompanySettings";
import { ForecastSettings } from "@/components/ForecastSettings";
import { api, errMsg, isConflict, notifyStockChanged } from "@/lib/api";
import { ConflictDialog } from "@/components/ConflictDialog";

const FIELDS = [
  ["fast_threshold", "Fast moving if avg monthly sale ≥", "units / month"],
  ["slow_threshold", "Slow moving if avg monthly sale <", "units / month"],
  ["overbought_months", "Over Bought if stock cover >", "months"],
  ["lowbought_months", "Low Bought if stock cover <", "months"],
  ["target_days", "Target stock days (reorder level) (not used)", "days"],
  ["dead_stock_threshold", "Dead stock if yearly sales <", "units / year"],
];

export default function Settings() {
  const { isAdmin } = useAuth();
  const [s, setS] = useState(null);
  const [conflict, setConflict] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loading />;
  const reload = () => api.get("/settings").then((r) => { setS(r.data); setConflict(false); toast.success("Latest settings loaded"); });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...Object.fromEntries(FIELDS.map(([k]) => [k, Number(s[k])])), expected_rev: s.rev || 0 };
      const { data } = await api.put("/settings", body);
      setS(data);
      toast.success("Thresholds saved — reports recalculated");
      notifyStockChanged();
    } catch (err) { if (isConflict(err)) setConflict(true); else toast.error(errMsg(err)); }
  };
  return (
    <div className="max-w-2xl">
      <PageHeader title="Threshold Settings" subtitle={isAdmin ? "These drive movement, stock position, reorder and dead stock calculations." : "Only admins can change thresholds."} />
      <form onSubmit={save} className="panel divide-y divide-border">
        {FIELDS.map(([k, label, unit]) => (
          <div key={k} className="flex items-center justify-between gap-4 p-4">
            <div><div className="text-sm font-medium">{label}</div><div className="text-xs text-muted-foreground">{unit}</div></div>
            <Input type="number" step="1" min="0" disabled={!isAdmin} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })}
              className="w-28 text-right font-mono" data-testid={`setting-${k}`} />
          </div>
        ))}
        {isAdmin && <div className="p-4 flex justify-end"><Button type="submit" data-testid="settings-save-btn">Save thresholds</Button></div>}
      </form>
      <ConflictDialog open={conflict} what="setting" onClose={() => setConflict(false)} onReload={reload} />
      <div className="mt-6 panel p-4 text-xs text-muted-foreground space-y-1.5">
        <div><b className="text-foreground">Avg monthly sale</b> = last 12 months total ÷ 12</div>
        <div><b className="text-foreground">Base</b> = weighted average of the 12 full months from the first month with a sale (w_old ÷ 6 for months 1–6, w_mid ÷ 3 for 7–9, w_new ÷ 3 for 10–12); if the avg of the last 3 months &gt; trend multiplier × avg of earlier months, base = at least that last-3 avg</div>
        <div><b className="text-foreground">Pace</b> = running-month sales ÷ today's day × days in month (India time)</div>
        <div><b className="text-foreground">Forecast 30D</b> = (from the min day: higher of base and (base + pace) ÷ 2; before it: base; no sales history: pace) × event uplift</div>
        <div><b className="text-foreground">Avg daily</b> = forecast 30D ÷ 30</div>
        <div><b className="text-foreground">Req. stock 30D</b> = round up(forecast 30D × safety factor)</div>
        <div><b className="text-foreground">Reorder qty</b> = req. stock 30D − current stock (all warehouses), minimum 0</div>
        <div><b className="text-foreground">Net to produce / Order qty</b> = reorder qty − open purchase / mfg order qty, minimum 0</div>
        <div><b className="text-foreground">Stock cover</b> = current stock ÷ avg monthly sale</div>
      </div>
      <ForecastSettings isAdmin={isAdmin} />
      <CompanySettings isAdmin={isAdmin} />
    </div>
  );
}
